import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ClientOnboardingApi from '../../apis/ClientOnboardingApi';
import LoadingCircle from '../../components/LoadingCircle/LoadingCircle';
import UnexpectedError from '../../components/UnexpectedError/UnexpectedError';
import { ClientOnboardingConfigDto } from '../../generated-types/clientOnboardingConfigDto';
import { ClientOnboardingStepKey } from '../../generated-types/clientOnboardingStepKey';
import { ClientStepStateDto } from '../../generated-types/clientStepStateDto';
import useApi from '../../lib/useApi';
import useFooterControl from '../../lib/useFooterControl';
import ClientOnboardingComplete from './components/ClientOnboardingComplete/ClientOnboardingComplete';
import NarrowStepper from './components/stepper/NarrowStepper';
import WideStepper from './components/stepper/WideStepper';
import BasicInformationStep from './components/steps/BasicInformationStep';
import LicenseAndPricingStep from './components/steps/LicenseAndPricingStep';
import MasterServiceAgreementStep from './components/steps/MasterServiceAgreement';
import OrganizationStep from './components/steps/OrganizationStep';
import PaymentStep from './components/steps/PaymentStep';
import WelcomeStep from './components/steps/WelcomeStep/WelcomeStep';
import CommonStepProps from './lib/CommonStepProps';
import StepDetails from './lib/StepDetails';

/**
 * The view for a client who is completing the onboarding
 */
const ClientOnboardingView = () => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });
  const [activeStep, setActiveStep] = useState<number>(-1);
  const [latestStep, setLatestStep] = useState<number>(-1);
  const [stepStates, setStepStates] = useState<ClientStepStateDto[]>();
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  const [showTitle, setShowTitle] = useState(true);
  const [configuration, setConfiguration] =
    useState<ClientOnboardingConfigDto>();
  useFooterControl(false);

  /**
   * This method sets up the common properties that are used among all the steps
   */
  const setupCommonStepProps = useCallback(
    (
      step: ClientOnboardingStepKey,
      argConfig: ClientOnboardingConfigDto,
      argStepStates: ClientStepStateDto[]
    ): CommonStepProps => {
      const msaStep = argStepStates.filter(
        (s) => s.stepKey == ClientOnboardingStepKey.MasterServiceAgreement
      );
      return {
        config: argConfig,
        isStepComplete: argStepStates.filter((s) => s.stepKey == step)[0]
          .complete,
        isMasterServiceAgreementStepComplete:
          msaStep.length > 0 ? msaStep[0].complete : false,
        moveToNextStep: () => setActiveStep((a) => a + 1),
        setStepComplete: () => {
          const nextStates = argStepStates.map((s) => {
            if (s.stepKey != step) return s;

            return {
              ...s,
              complete: true,
            };
          });
          setStepStates(nextStates);
        },
        showError: () => setShowError(true),
        hideTitle: () => setShowTitle(false),
      };
    },
    []
  );

  /**
   * A map that maps a particular client onboarding step key to it's details
   */
  const stepDetailsMap = useMemo<Map<ClientOnboardingStepKey, StepDetails>>(
    () =>
      new Map(
        stepStates && stepStates.length > 0 && configuration
          ? [
              [
                ClientOnboardingStepKey.Welcome,
                {
                  stepContent: (
                    <WelcomeStep
                      {...setupCommonStepProps(
                        ClientOnboardingStepKey.Welcome,
                        configuration,
                        stepStates
                      )}
                    />
                  ),
                },
              ],
              [
                ClientOnboardingStepKey.BasicInformation,
                {
                  stepContent: (
                    <BasicInformationStep
                      {...setupCommonStepProps(
                        ClientOnboardingStepKey.BasicInformation,
                        configuration,
                        stepStates
                      )}
                    />
                  ),
                },
              ],
              [
                ClientOnboardingStepKey.LicenseAndPricing,
                {
                  stepContent: (
                    <LicenseAndPricingStep
                      {...setupCommonStepProps(
                        ClientOnboardingStepKey.LicenseAndPricing,
                        configuration,
                        stepStates
                      )}
                    />
                  ),
                },
              ],
              [
                ClientOnboardingStepKey.MasterServiceAgreement,
                {
                  stepContent: (
                    <MasterServiceAgreementStep
                      {...setupCommonStepProps(
                        ClientOnboardingStepKey.MasterServiceAgreement,
                        configuration,
                        stepStates
                      )}
                    />
                  ),
                },
              ],
              [
                ClientOnboardingStepKey.Payment,
                {
                  stepContent: (
                    <PaymentStep
                      {...setupCommonStepProps(
                        ClientOnboardingStepKey.Payment,
                        configuration,
                        stepStates
                      )}
                    />
                  ),
                },
              ],
              [
                ClientOnboardingStepKey.OrganizationSetup,
                {
                  stepContent: (
                    <OrganizationStep
                      {...setupCommonStepProps(
                        ClientOnboardingStepKey.OrganizationSetup,
                        configuration,
                        stepStates
                      )}
                    />
                  ),
                },
              ],
            ]
          : []
      ),
    [configuration, setupCommonStepProps, stepStates]
  );

  /**
   * This array is used to build and display all the steps
   */
  const steps = useMemo<(StepDetails & ClientStepStateDto)[]>(() => {
    if (!stepStates) return [];

    const stepBuilder: (StepDetails & ClientStepStateDto)[] = [];

    for (const stepState of stepStates) {
      stepBuilder.push({
        ...stepState,
        ...stepDetailsMap.get(stepState.stepKey)!,
      });
    }

    return stepBuilder;
  }, [stepStates, stepDetailsMap]);
  const clientOnboardingApi = useApi(ClientOnboardingApi);

  useEffect(() => {
    // Load the step states
    clientOnboardingApi
      .GetStepStates()
      .then(setStepStates)
      .catch(() => {
        setShowError(true);
      });

    // Load the configuration
    clientOnboardingApi
      .GetClientOnboardingConfig()
      .then(setConfiguration)
      .catch(() => {
        setShowError(true);
      });

    // Check the validity
    clientOnboardingApi
      .IsValid()
      .then((v) => {
        setIsExpired(!v);
        if (v) setIsLoading(false);
      })
      .catch(() => setShowError(true));
  }, [clientOnboardingApi]);

  useEffect(() => {
    // Once we've loaded the step states and the configuration, then we can turn off the loading circle
    if (configuration && stepStates && stepStates.length > 0) {
      setIsLoading(false);
    }
  }, [configuration, stepStates]);

  useEffect(() => {
    if (!stepStates) return;

    for (let i = 0; i < stepStates.length; i++) {
      const stepState = stepStates[i];

      if (!stepState.complete) {
        setLatestStep(i);

        if (activeStep == -1) {
          setActiveStep(i);
        }

        break;
      }
    }
  }, [activeStep, latestStep, stepStates]);

  useEffect(() => {
    if (showError) setIsLoading(false);
  }, [showError]);

  /**
   * The main content for the client onboarding page
   */
  const mainContent = useMemo(() => {
    // Show the unexpected error component
    if (showError)
      return <UnexpectedError errorContext="the client onboarding page" />;

    // If all the step states are complete, then the user has already completed the client onboarding
    if (
      stepStates &&
      stepStates.length > 0 &&
      stepStates.every((s) => s.complete)
    )
      return <ClientOnboardingComplete />;

    // If the steps aren't set yet, return early
    if (latestStep == -1 || activeStep == -1) {
      return <></>;
    }

    // Show that the client onboarding is no longer valid
    if (isExpired)
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          height="100%"
          justifyContent="center"
          gap=".5em"
        >
          <Box display="flex" gap="1em" alignItems="center">
            <Typography variant="h1" fontSize="3em">
              Onboarding has expired
            </Typography>
            <WarningAmberRoundedIcon color="warning" sx={{ fontSize: '3em' }} />
          </Box>
          <Typography fontSize="1.1em">
            Please contact us for assistance
          </Typography>
        </Box>
      );

    if (isMd)
      return (
        <WideStepper
          activeStep={activeStep}
          latestStep={latestStep}
          setActiveStep={setActiveStep}
          steps={steps}
          showTitle={showTitle}
        />
      );

    return (
      <NarrowStepper
        activeStep={activeStep}
        latestStep={latestStep}
        setActiveStep={setActiveStep}
        steps={steps}
        showTitle={showTitle}
      />
    );
  }, [
    activeStep,
    isExpired,
    isMd,
    latestStep,
    showError,
    showTitle,
    stepStates,
    steps,
  ]);

  /**
   * Whenever the step changes, show the title by default
   */
  useEffect(() => {
    setShowTitle(true);
  }, [activeStep]);

  return (
    <Box display="flex" flexDirection="column" width="100%" height="100%">
      {mainContent}
      <LoadingCircle isLoading={isLoading} />
    </Box>
  );
};

export default ClientOnboardingView;
